import { useNavigate } from "react-router-dom";
import { highlightMatch } from "@/lib/highlightText";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/contexts/LanguageContext";
import { countTermLanguageMeanings } from "@/lib/termLanguageCount";
import { wordFirstLetter } from "@/lib/normalizeText";
import { ArrowRight, Volume2 } from "lucide-react";
import { motion } from "framer-motion";

/**
 * Returns a ~`windowLen` character excerpt centered around the first occurrence
 * of `query` in `text`. Falls back to the beginning if the term is not found.
 * Adds "…" prefix/suffix when the excerpt doesn't start/end at a text boundary.
 */
function excerptAround(text: string, query: string, windowLen = 150): string {
  if (!text) return "";
  const q = query.trim();
  if (!q) {
    return text.length > windowLen ? text.slice(0, windowLen) + "…" : text;
  }
  const matchIdx = text.toLowerCase().indexOf(q.toLowerCase());
  if (matchIdx === -1) {
    return text.length > windowLen ? text.slice(0, windowLen) + "…" : text;
  }
  // Put ~40% of the window before the match so the match isn't at the very edge.
  let start = Math.max(0, matchIdx - Math.floor(windowLen * 0.4));
  let end = start + windowLen;
  if (end > text.length) {
    end = text.length;
    start = Math.max(0, end - windowLen);
  }
  // Avoid cutting mid-word at the left boundary.
  if (start > 0) {
    const space = text.indexOf(" ", start);
    if (space !== -1 && space < matchIdx) start = space + 1;
  }
  const prefix = start > 0 ? "…" : "";
  const suffix = end < text.length ? "…" : "";
  return prefix + text.slice(start, end) + suffix;
}


interface TermCardProps {
  id: string;
  word: string;
  meaningEnglish?: string | null;
  meaningHindi?: string | null;
  meaningGujarati?: string | null;
  pronunciation?: string | null;
  index?: number;
  /** Search query — highlights occurrences in the meaning text. */
  highlight?: string;
  /** True when this term's word exactly matches the search query (no highlight needed). */
  isExactMatch?: boolean;
}

export function TermCard({
  id,
  word,
  meaningEnglish,
  meaningHindi,
  meaningGujarati,
  pronunciation,
  index = 0,
  highlight,
  isExactMatch = false,
}: TermCardProps) {
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  const primaryMeaning =
    language === "hi"
      ? meaningHindi || meaningEnglish || meaningGujarati
      : language === "gu"
        ? meaningGujarati || meaningEnglish || meaningHindi
        : meaningEnglish || meaningHindi || meaningGujarati;

  const languageCount = countTermLanguageMeanings({
    meaningEnglish,
    meaningHindi,
    meaningGujarati,
  });

  const languagesLabel =
    languageCount <= 0
      ? t("termCard.viewTerm")
      : languageCount === 1
        ? t("termCard.viewOneLanguage", { count: languageCount })
        : t("termCard.viewAllLanguages", { count: languageCount });

  const truncate = (text: string | null | undefined, len = 80) => {
    if (!text) {
      return null;
    }
    return text.length > len ? text.slice(0, len) + "…" : text;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.4,
        delay: index * 0.06,
        ease: [0.25, 0.46, 0.45, 0.94],
      }}
    >
      <Card
        onClick={() => {
          const dest = highlight
            ? `/term/${id}?highlight=${encodeURIComponent(highlight)}`
            : `/term/${id}`;
          navigate(dest);
        }}
        className="border-border/40 hover:border-primary/30 group h-full cursor-pointer overflow-hidden bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
      >
        {/* Top accent */}
        <div className="from-primary/60 via-gold/40 to-primary/60 h-0.5 bg-gradient-to-r opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

        <CardHeader className="px-5 pb-2 pt-5">
          <div className="flex items-start justify-between gap-3">
            <CardTitle className="text-foreground group-hover:text-primary line-clamp-1 font-serif text-xl transition-colors duration-200 md:text-2xl">
              {highlight && !isExactMatch ? highlightMatch(word, highlight) : word}
            </CardTitle>
            <div className="flex shrink-0 items-center gap-1.5">
              {isExactMatch && (
                <Badge className="border-0 bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">
                  {t("termCard.exactMatch")}
                </Badge>
              )}
              <Badge
                variant="secondary"
                className="bg-primary/10 text-primary group-hover:bg-primary border-0 text-sm font-bold transition-all duration-300 group-hover:text-white"
              >
                {wordFirstLetter(word) || word.charAt(0).toUpperCase()}
              </Badge>
            </div>
          </div>
          {pronunciation && (
            <div className="text-muted-foreground mt-1 flex items-center gap-2">
              <Volume2 className="h-3.5 w-3.5" />
              <p className="text-base italic">/{pronunciation}/</p>
            </div>
          )}
        </CardHeader>

        <CardContent className="px-5 pb-5 pt-1">
          {primaryMeaning && (
            <p className="text-foreground/80 line-clamp-3 text-sm leading-relaxed">
              {highlight && !isExactMatch
                ? highlightMatch(excerptAround(primaryMeaning, highlight, 150), highlight)
                : truncate(primaryMeaning, 150)}
            </p>
          )}
          <div className="flex items-center justify-end pt-3">
            <span className="text-muted-foreground group-hover:text-primary mr-1 text-xs transition-colors">
              {languagesLabel}
            </span>
            <ArrowRight className="text-muted-foreground group-hover:text-primary h-4 w-4 transition-all duration-300 group-hover:translate-x-1" />
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
